import { Help } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { get, set } from 'lodash';
import React, { FC, useMemo } from 'react';
import { FORM_FIELDS_TYPES } from '../../../../../lib/constants/constant';
import { MUIFieldProps } from '../../../../../lib/types/types';
import { DisplayDate, DisplayDecimal, DisplayEuro } from '../../../../../utils/formats';
import { mapTablePath } from '../../../../../utils/formStruc';
import { enumT, fieldNameTranslation, isTrue } from '../../../../../utils/helper';
import MUIToolTip from '../../../mui-tooltip/MUIToolTip';
import FormFields from '../FormFields';
import SingleUpload from './SingleUpload';

const EditableTable: FC<MUIFieldProps> = (props) => {
	const { fieldValue, updateFieldValue, isDisabled } = props;

	const component = get(props, 'component');
	const componentPart = component.split('.');
	const apiName = useMemo(() => {
		if (get(props, 'api')) {
			return get(props, 'api');
		} else {
			return componentPart[0] + 's';
		}
	}, [componentPart, props]);

	const columns = get(props, 'schema.attributes');
	const isFieldDisabled = (columnKey: string) => {
		if (isDisabled) return true;
		if (get(props, 'disabledColumns', []).includes(columnKey)) {
			return true;
		} else return false;
	};
	const tablePath = get(props, 'path', '');
	const width = get(props, 'width', '100%');

	const handleAddRow = () => {
		let newValue = Object.keys(columns).map((key) => {
			return { [key]: null };
		});
		let newElement = [...fieldValue, newValue[0]];
		set(fieldValue, `${fieldValue.length}`, ...newValue);

		let e = { target: { value: newElement } };
		updateFieldValue(e as unknown as React.ChangeEvent<HTMLInputElement>);
	};
	const handleChange = (path: any, value: any, rowIndex: number) => {
		// Prüfe ob die Spalte Teil einer Radiogruppe ist
		const radioGroups = get(props, 'tableProps.radioGroups', []);
		if (radioGroups.includes(path) && value === true) {
			// Setze alle anderen Radiogruppen-Spalten in dieser Zeile auf false
			radioGroups.forEach((group: string) => {
				if (group !== path) {
					set(fieldValue, `${rowIndex}.${group}`, false);
				}
			});
		}

		set(fieldValue, `${rowIndex}.${path}`, value);

		let e = { target: { value: fieldValue } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};
	const handleDelete = (rowIndex: number) => {
		const newRow = fieldValue.filter((_: any, index: number) => index !== rowIndex);

		let e = { target: { value: newRow } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};

	const visibleColumns = Object.keys(columns).filter((key: string) => !get(props, 'hideColumns', []).includes(key));

	const showColGroups = isTrue(get(props, 'colGroups'));

	const startingKey = get(props, 'colGroupsProps.startingKey');
	const colSpan = get(props, 'colGroupsProps.span', 1);
	const label = get(props, 'colGroupsProps.label', '');
	const spannedColTooltip = get(props, 'colGroupsProps.tooltip', '');
	const preventLastElementDelete = get(props, 'preventLastElementDelete', false);
	const shouldHideCell = (columnKey: string, row: any, rowIndex: number) => {
		const hideCell = get(props, `fieldProps.${columnKey}.hideCell`);
		return typeof hideCell === 'function' ? hideCell(row, rowIndex) : false;
	};
	const getDynamicFieldProps = (columnKey: string, row: any, rowIndex: number) => {
		const getFieldProps = get(props, `fieldProps.${columnKey}.getFieldProps`);
		return typeof getFieldProps === 'function' ? getFieldProps(row, rowIndex) : {};
	};

	const bulletpointRaw = get(props, 'bulletpoint', false);
	const bulletpoint: false | 'bullet' | 'numbered' =
		bulletpointRaw === true ? 'numbered'
		: bulletpointRaw === 'bullet' ? 'bullet'
		: bulletpointRaw === 'numbered' ? 'numbered'
		: false;
	let skip = 0;
	return (
		<>
			<Box sx={{ mt: '1rem' }}>
				{get(props, 'tableLabel', '') && (
					<Typography sx={{ mb: 1, fontWeight: 600 }}>
						{get(props, 'tableLabel', '')}{' '}
						{get(props, 'tableLabelTooltip', '') ?
							<MUIToolTip title={get(props, 'tableLabelTooltip', '')}>
								<Help fontSize='small' color='primary' sx={{ ml: '5px' }} />{' '}
							</MUIToolTip>
						:	<></>}
					</Typography>
				)}
				<TableContainer component={'div'} sx={{ width: width }}>
					<Table className='editable-table'>
						<TableHead>
							{showColGroups && (
								<TableRow>
									{bulletpoint && <TableCell sx={{ width: 40 }} />}
									{/* eslint-disable-next-line array-callback-return */}
									{visibleColumns.map((eachCol: string, i: number) => {
										if (skip > 0) {
											skip--;
											return null; // überspringen
										}
										if (eachCol === startingKey) {
											skip = colSpan - 1; // die nächsten colSpan-1 Zellen überspringen
											return (
												<TableCell key={startingKey + i} colSpan={colSpan}>
													{label}
													{spannedColTooltip && (
														<MUIToolTip title={spannedColTooltip}>
															<Help fontSize='small' color='primary' sx={{ ml: '5px' }} />
														</MUIToolTip>
													)}
												</TableCell>
											);
										} else {
											return <TableCell key={startingKey + i} rowSpan={1} />;
										}
									})}
								</TableRow>
							)}
							<TableRow>
								{bulletpoint && <TableCell sx={{ width: 40 }} />}
								{visibleColumns.map((key) => {
									const colDef = columns[key];
									return (
										<TableCell
											key={key}
											align={isFieldDisabled(key) && ['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ? 'right' : 'left'}
											sx={{
												padding: '0.5rem',
												width: get(props, `fieldProps.${key}.width`, 'auto'),
												maxWidth: get(props, `fieldProps.${key}.maxWidth`, '0'),
												writingMode: get(props, `fieldProps.${key}.wirtingMode`, 'initial'),
											}}
										>
											<>
												{getColumnLabel(get(colDef, 'path'), key, get(props, 'fieldProps'), apiName)}
												{getToolTip(get(props, 'fieldProps', {}), key) && (
													<MUIToolTip title={getToolTip(get(props, 'fieldProps', {}), key)}>
														<Help fontSize='small' color='primary' sx={{ ml: '5px' }} />
													</MUIToolTip>
												)}
											</>
										</TableCell>
									);
								})}
								{!get(props, 'hideDeleteButton') && !isDisabled && (
									<TableCell sx={{ padding: '0.5rem 1rem', backgroundColor: 'background.paper' }} className='pinned-right-column'>
										{!get(props, 'hideAddButton') && (
											<IconButton onClick={() => handleAddRow()} disabled={isDisabled || fieldValue.length >= get(props, 'maxLevel', 999)}>
												<AddCircleOutlineIcon />
											</IconButton>
										)}
									</TableCell>
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{(fieldValue || []).map((eachRow: any, rowIndex: number) => {
								return (
									<TableRow key={rowIndex}>
										{bulletpoint && (
											<TableCell sx={{ width: 40, textAlign: 'center' }}>
												<Typography variant='body2'>{bulletpoint === 'numbered' ? `${rowIndex + 1}.` : '•'}</Typography>
											</TableCell>
										)}
										{visibleColumns.map((columnKey) => {
											// overwrite column definition with dynamic field props if available
											const dynamicFieldProps = getDynamicFieldProps(columnKey, eachRow, rowIndex);
											const colDef = { ...columns[columnKey], ...dynamicFieldProps };
											const fieldKey = get(dynamicFieldProps, 'fieldKey', columnKey);
											const attributePathWithIndex = mapTablePath(colDef.path, rowIndex, tablePath);
											const fieldValue = get(dynamicFieldProps, 'fieldValue', eachRow[fieldKey]);
											const hideCell = shouldHideCell(columnKey, eachRow, rowIndex);
											return (
												<TableCell
													key={columnKey}
													sx={{ padding: '0.5rem', maxWidth: 0, minWidth: get(props, `fieldProps.${columnKey}.width`, 'auto') }}
													align={
														isFieldDisabled(columnKey) && ['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ?
															'right'
														:	'left'
													}
												>
													{hideCell ?
														''
													: isFieldDisabled(columnKey) ?
														<Typography>
															{getDisplayText(
																colDef.type,
																fieldValue,
																get(props, `fieldProps.${columnKey}.format`, ''),
																get(props, `fieldProps.${columnKey}.customLabel`, ''),
																get(props, `fieldProps.${columnKey}.pathForLabel`, ''),
															)}
														</Typography>
													:	<FormFields
															{...colDef}
															label=''
															type={get(props, `fieldProps.${columnKey}.type`, colDef.type)}
															formValues={{ ...eachRow, [fieldKey]: fieldValue }}
															formErrors={get(props, 'formErrors')}
															format={get(props, `fieldProps.${columnKey}.format`)}
															path={fieldKey}
															onChange={(path, value) => {
																handleChange(path, value, rowIndex);
															}}
															disabled={
																isDisabled ||
																get(props, 'disabledColumns', []).includes(columnKey) ||
																get(dynamicFieldProps, 'disabled', false)
															}
															editableTablePathWIthIndex={attributePathWithIndex}
															allowOnlyImage={get(props, `fieldProps.${columnKey}.allowOnlyImage`)}
															size='small'
															options={get(props, `fieldProps.${columnKey}.options`, colDef.options)}
															multiple={get(props, `fieldProps.${columnKey}.multiple`)}
															stringMode={get(props, `fieldProps.${columnKey}.stringMode`)}
															separator={get(props, `fieldProps.${columnKey}.separator`)}
															maxCharLength={get(props, `fieldProps.${columnKey}.maxCharLength`)}
														/>
													}
												</TableCell>
											);
										})}

										{!get(props, 'hideDeleteButton') && !isDisabled && (
											<TableCell className='pinned-right-column' sx={{ padding: '0.5rem 1rem', backgroundColor: 'background.paper' }}>
												<IconButton
													onClick={() => handleDelete(rowIndex)}
													disabled={isDisabled || (preventLastElementDelete && rowIndex === (fieldValue || []).length - 1)}
												>
													<DeleteIcon />
												</IconButton>
											</TableCell>
										)}
									</TableRow>
								);
							})}
							{get(props, 'sumRow', []).map((lastRow: any) => {
								return (
									<TableRow sx={{ backgroundColor: 'background.paper' }}>
										{visibleColumns.map((columnKey) => {
											const colDef = columns[columnKey];
											return (
												<TableCell
													key={columnKey}
													sx={{ padding: '0.5rem 1rem' }}
													align={['integer', 'decimal', 'date'].includes(get(colDef, 'type')) ? 'right' : 'left'}
												>
													<Typography>
														{getDisplayText(colDef.type, lastRow[columnKey], get(props, `fieldProps.${columnKey}.format`))}
													</Typography>
												</TableCell>
											);
										})}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</>
	);
};
export default EditableTable;

function getColumnLabel(path: string, key: string, fieldProps: any, apiName: string): string {
	if (fieldProps && fieldProps[key] && fieldProps[key].label) {
		return fieldProps[key].label;
	}

	const parts: string[] = path.split('.');
	const lastPart: string = parts.pop()!;

	if (lastPart.startsWith('Zelle')) {
		// Extract the number from the last part
		const zelleNumber: string = lastPart.match(/\d+/)![0];
		// Modify the second to last part, which contains Block_X
		let blockPart: string = parts.pop()!;
		blockPart = blockPart.includes('Block_') ? blockPart : blockPart;

		// const modifiedBlockPart: string = blockPart.replace(/Block_\d+/, `Spalte_${zelleNumber}`);
		const modifiedBlockPart: string = blockPart + `_Spalte_${zelleNumber}`;
		return fieldNameTranslation(modifiedBlockPart, apiName);
	} else {
		return fieldNameTranslation(lastPart, apiName);
	}
}

function getToolTip(fieldProps: any, key: string) {
	return get(fieldProps, `${key}.tooltip`, '');
}

function getDisplayText(type: string, value: any, format?: string, label?: string, keyForlabel?: string) {
	if (value === undefined) return '';
	if (label) return label;

	switch (type) {
		case 'decimal':
			if (format === 'currency') {
				return DisplayEuro(value);
			}
			if (format === 'percentage') {
				return DisplayDecimal(value, true);
			}
			return DisplayDecimal(value);
		case 'date':
			return DisplayDate(value);
		case 'enumeration':
			return enumT(value);
		case 'boolean':
			return isTrue(value) ? 'Ja' : 'Nein';
		case 'relation':
			return get(value, keyForlabel, '');
		case FORM_FIELDS_TYPES.media:
			return <SingleUpload label={''} fieldValue={value} updateFieldValue={(_e) => {}} isDisabled={true} error={false} errorMessage={''} />;
		default:
			return value;
	}
}
